import React, { useEffect, useState } from "react";
import { Paper, Typography, Box, Chip, CircularProgress } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { getRecentActivities, getActivityIcon, Activity } from "../../utils/ActivityLog";

interface ActivityCardProps {
  patientId?: string;
  limit?: number;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ patientId, limit = 5 }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (patientId) {
      try {
        const recentActivities = getRecentActivities(patientId, limit);
        setActivities(recentActivities);
      } catch (error) {
        console.error("Error loading activities:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [patientId, limit]);

  return (
    <Paper sx={{ flex: "1 1 300px", p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <HistoryIcon fontSize="small" />
        <Typography fontWeight="bold">Activity Log</Typography>
        {activities.length > 0 && (
          <Chip
            label={activities.length}
            size="small"
            variant="outlined"
            sx={{ ml: "auto" }}
          />
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress size={24} />
        </Box>
      ) : activities.length > 0 ? (
        <Box 
          sx={{ 
            maxHeight: "180px", 
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555",
              },
            },
          }}
        >
          {activities.map((activity) => (
            <Box
              key={activity.id}
              sx={{
                display: "flex",
                gap: 1.5,
                py: 1,
                borderBottom: "1px solid #eee",
                "&:last-child": {
                  borderBottom: "none",
                },
              }}
            >
              <Typography sx={{ fontSize: "1.2em", minWidth: "20px" }}>
                {getActivityIcon(activity.type)}
              </Typography>
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: "break-word",
                    color: "#333",
                  }}
                >
                  {activity.description}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {new Date(activity.timestamp).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box mt={3} display="flex" flexDirection="column" alignItems="center">
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No activities yet. Book an appointment or update your profile to get started!
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ActivityCard;
